import {
    catchError,
    concatMap,
    defaultIfEmpty,
    ignoreElements,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { concat, defer, EMPTY, Observable, of } from 'rxjs';
import { Actions, catchableUnauthenticated } from 'src/core/state/user';
import { execute } from 'swagger/ts/WorkwearExpressBasketCartManagementV1SwapCustomerCartsPost';
import { Deps } from 'src/types/global-types';
import { createDebug } from 'src/util/debug';
import { fetchMineCartId } from 'src/core/state/cart/utils/fetchMineCartId';
import { fetchCart } from 'src/features/cart/epics/fetchCart';
import { Msg } from 'src/core/state/cart';
import { DataStatus } from 'src/core/engine/types';
import { BasketMsg } from 'src/components/Basket/feature/basket.actions';
import { clearApolloStore } from 'src/features/user/feature/epics/clearApolloStoreOnAuthChange.epic';
import Bugsnag from '@bugsnag/js';

import { mergeGuestCart } from './mergeGuestCart';

const debug = createDebug('userSignedInReaction');
const SWAP_FAILED = { swapFailed: true } as const;
const SWAP_OK = { swapFailed: false } as const;

/**
 * Listen for the user signing in
 *
 * When that happens, attempt to refresh the cart
 *
 * @param action$
 * @param state$
 * @param deps
 */
export function userSignedInReaction(action$: Observable<any>, state$: Observable<any>, deps: Deps) {
    return action$.pipe(
        ofType<Actions>('User.SignInSuccess'),
        withLatestFrom(state$.pipe(map((s) => s?.cart))),
        mergeMap(([, cart]) => {
            const items = cart?.items || [];
            const hasGuestItems = items.length > 0 || Number(cart?.items_qty) > 0;
            // Finish the account-scoped cache wipe before cart GraphQL so
            // clearStore cannot cancel getCustomerCartId / getCart.
            return concat(
                clearApolloStore(deps),
                of(Msg('Cart.SetStatus', DataStatus.Pending)),
                defer(() => refreshCart(hasGuestItems, cart?.cartId, deps)),
                of(Msg('Cart.SetStatus', DataStatus.Success)),
            );
        }),
    );
}

function refreshCart(hasGuestItems: boolean, guestCartId: string | number | null, deps: Deps) {
    if (!hasGuestItems || !guestCartId) {
        return applyFetchedCart(mineFetch(deps), false);
    }

    return applyFetchedCart(transferThenFetch(String(guestCartId), deps), true);
}

/**
 * Assign the guest quote to the customer (same item IDs, so customisations
 * survive), then load whichever GraphQL cart actually contains those items.
 * mergeCarts is only used if swap fails — it copies SKUs onto a new quote
 * and drops Workwear customisation rows.
 */
function transferThenFetch(guestCartId: string, deps: Deps) {
    return execute({ guestCartId }, deps).pipe(
        tap(() => debug('transferred guest cart to account cart')),
        ignoreElements(),
        catchError((error) => {
            Bugsnag.notify(error);
            debug('REST cart swap failed, falling back to mergeCarts');
            return of(SWAP_FAILED);
        }),
        defaultIfEmpty(SWAP_OK),
        concatMap((result) => {
            if (result === SWAP_FAILED) {
                return mergeThenFetch(guestCartId, deps);
            }
            return fetchSwappedCart(guestCartId, deps);
        }),
    );
}

function fetchSwappedCart(guestCartId: string, deps: Deps) {
    return fetchMineCartId(deps).pipe(
        switchMap((customerCartId) => {
            if (String(customerCartId) === guestCartId) {
                return fetchCart(customerCartId, deps);
            }

            return fetchCart(customerCartId, deps).pipe(
                switchMap((customerCart) => {
                    if (customerCart?.items?.length) {
                        return of(customerCart);
                    }
                    debug('customer cart empty after swap, fetching guest cart %s', guestCartId);
                    return fetchCart(guestCartId, deps);
                }),
            );
        }),
    );
}

function mergeThenFetch(guestCartId: string, deps: Deps) {
    return fetchMineCartId(deps).pipe(
        switchMap((customerCartId) => {
            if (String(customerCartId) === guestCartId) {
                return fetchCart(customerCartId, deps);
            }

            return mergeGuestCart(guestCartId, customerCartId, deps).pipe(
                tap((mergedId) => debug('fell back to mergeCarts into account cart %s', mergedId)),
                switchMap((mergedId) => fetchCart(mergedId, deps)),
                catchError((error) => {
                    Bugsnag.notify(error);
                    return fetchCart(customerCartId, deps);
                }),
            );
        }),
    );
}

function mineFetch(deps: Deps) {
    return fetchMineCartId(deps).pipe(switchMap((id) => fetchCart(id, deps)));
}

function applyFetchedCart(cart$: Observable<any>, preserveGuestItems: boolean) {
    return cart$.pipe(
        switchMap((resp) => {
            const fetchedCount = resp?.items?.length || 0;
            /**
             * customerCart is often empty. Never replace a guest basket with that
             * empty account cart after sign-in.
             */
            if (preserveGuestItems && fetchedCount === 0) {
                return EMPTY;
            }
            return of(Msg('Cart.FetchSuccess', resp), BasketMsg('Basket.Refresh'), Msg('Cart.Persist'));
        }),
        catchableUnauthenticated(({ error }) => {
            Bugsnag.notify(error);
            return EMPTY;
        }),
    );
}
